import { Fit, Layout, useRive } from '@rive-app/react-canvas';
import { ForceTheme } from 'src/common/components/force-theme';
import { ThemeColorMode } from 'src/common/enums';
import { useGetUserQuery } from 'src/common/slices';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { SubscribeNowLabel, useTrackSubscribeNow } from 'src/features/campaigns/hooks/use-track-subscribe-now';
import { useYearlyPricing } from 'src/features/campaigns/hooks/use-yearly-pricing';
import { Countdown } from '../../components/countdown';
import { useGetAfterSignupFlashSaleEndTime } from '../../hooks/use-get-after-signup-flash-sale-end-time';
import { useSubscribeNowCtaLink } from '../../hooks/use-subscribe-now-cta-link';
import { CopyType, getModalCopy } from '../modal/modal.constants';
import { SubscribeButton } from '../subscribe-button';
import { StyledBanner, StyledButtons, StyledCountdownContainer, StyledRiveAnimation, StyledSubTitle, StyledSubTitleAlt, StyledTitle } from './banner.styles';
import type { AfterSignupFlashSaleProps } from './banner.types';
import { DiscountMessage } from './components/discount-message';
export const AfterSignupFlashSaleBanner = ({
  title,
  subtitle,
  subtitleAlt,
  isLarge,
  ctaLabel,
  ctaName,
  onCtaClick
}: AfterSignupFlashSaleProps) => {
  const userQuery = useGetUserQuery();
  const {
    campaign
  } = useActiveCampaign();
  const {
    yearlyPrice,
    discountedYearlyPrice,
    symbol
  } = useYearlyPricing();
  const ctaLink = useSubscribeNowCtaLink(campaign);
  const trackSubscribeNow = useTrackSubscribeNow();
  const user = userQuery.data?.data?.user;
  const isPremium = Boolean(user?.isPremium);
  const flashSaleEndTime = useGetAfterSignupFlashSaleEndTime(campaign, user?.dateSignUp);
  const {
    RiveComponent: AfterSignupRiveComponent
  } = useRive({
    src: '/images/animations/campaigns/after-signup.riv',
    autoplay: true,
    layout: new Layout({
      fit: Fit.FitHeight
    })
  });
  if (!campaign) return null;
  const onClick = () => {
    trackSubscribeNow(ctaName);
    onCtaClick?.();
  };
  return <ForceTheme themeMode={ThemeColorMode.DARK} data-sentry-element="ForceTheme" data-sentry-component="AfterSignupFlashSaleBanner" data-sentry-source-file="banner.tsx">
      <StyledBanner data-sentry-element="StyledBanner" data-sentry-source-file="banner.tsx">
        <StyledRiveAnimation data-sentry-element="StyledRiveAnimation" data-sentry-source-file="banner.tsx">
          <AfterSignupRiveComponent data-sentry-element="AfterSignupRiveComponent" data-sentry-source-file="banner.tsx" />
        </StyledRiveAnimation>
        <StyledTitle data-testid="campaign-title" data-sentry-element="StyledTitle" data-sentry-source-file="banner.tsx">{title}</StyledTitle>
        {subtitle && <StyledSubTitle data-testid="campaign-promotion-text">{subtitle}</StyledSubTitle>}
        {subtitleAlt && <StyledSubTitleAlt data-testid="campaign-promotion-text-alt">{subtitleAlt}</StyledSubTitleAlt>}
        <StyledCountdownContainer data-sentry-element="StyledCountdownContainer" data-sentry-source-file="banner.tsx">
          <Countdown vertical dark includeDays endTime={flashSaleEndTime} data-sentry-element="Countdown" data-sentry-source-file="banner.tsx" />
        </StyledCountdownContainer>
        <DiscountMessage isPremium={isPremium} yearlyPrice={yearlyPrice} discountedYearlyPrice={discountedYearlyPrice} symbol={symbol} data-sentry-element="DiscountMessage" data-sentry-source-file="banner.tsx" />
        <StyledButtons data-sentry-element="StyledButtons" data-sentry-source-file="banner.tsx">
          <SubscribeButton to={ctaLink} aria-label={ctaLabel} onClick={onClick} data-sentry-element="SubscribeButton" data-sentry-source-file="banner.tsx">
            {ctaLabel}
          </SubscribeButton>
        </StyledButtons>
      </StyledBanner>
    </ForceTheme>;
};
export const withModalStyling = (BaseComponent: typeof AfterSignupFlashSaleBanner) => {
  return function ModalCampaignBanner(props: {
    onClick: () => void;
    goal?: string;
    isPremium?: boolean;
  }) {
    const {
      isPremium,
      goal,
      onClick
    } = props;
    const copy = getModalCopy(isPremium ? CopyType.PREMIUM : CopyType.FREE, goal);
    const ctaLabel = isPremium ? 'Switch to annual' : 'Subscribe now';
    return <BaseComponent title={copy.title} subtitle={copy.subtitle} subtitleAlt={undefined} isLarge={false} ctaLabel={`${ctaLabel} & save 25%`} ctaName={SubscribeNowLabel.FlashSaleModal} onCtaClick={onClick} />;
  };
};